// V4.3 — central parser + surfacer for HTTP 402 payment/quota responses.
//
// Two client paths reach the edge functions and BOTH must funnel 402s here
// so a quota cap NEVER renders as "conexão interrompida":
//   A. Raw fetch + SSE stream (MirrorChat + the 4 mini-mirrors). A 402 arrives
//      as a normal Response with status 402 — read its JSON body, don't treat
//      the stream as dead.
//   B. supabase.functions.invoke() (task-extract, agenda-extract,
//      language-lesson, language-react, sleep-react, …). A non-2xx surfaces as
//      FunctionsHttpError whose `.context` is the raw Response carrying the
//      402 JSON.
//
// Backend contracts (see supabase/functions/_shared/v43Quotas.ts):
//   quota_exceeded { error, kind:"tts"|"live"|"text", scope:"daily"|"monthly",
//                    used, limit, period_start, period_end,
//                    current_tier, upgrade_tier }
//   module_locked  { error, module, current_tier, upgrade_tier }

// "image" (F2): cap mensal de imagens do deliverable-generate — o paywall
// mostra copy própria (paywall.quota.image).
// "stt" (06/08): transcrição Whisper ganhou cota mensal em segundos. Sem esta
// entrada o 402 de STT cairia no coerce para "text" abaixo e o usuário leria
// "suas mensagens acabaram" depois de falar no microfone.
export type QuotaKind = "tts" | "live" | "text" | "image" | "stt" | "email" | "files";
export type QuotaScope = "daily" | "monthly";
export type PlanTier = "free" | "premium" | "pro" | "ultra";

export interface QuotaExceeded {
  type: "quota_exceeded";
  kind: QuotaKind;
  scope: QuotaScope;
  used: number;
  limit: number;
  periodEnd: string;
  currentTier: PlanTier;
  upgradeTier: PlanTier | null;
}

export interface ModuleLocked {
  type: "module_locked";
  module: string;
  currentTier: PlanTier;
  upgradeTier: PlanTier | null;
}

export type PaymentError = QuotaExceeded | ModuleLocked;

function asPlanTier(v: unknown): PlanTier {
  return v === "premium" || v === "pro" || v === "ultra" ? v : "free";
}

/**
 * Typed throwable for stream helpers that can't surface UX themselves (no
 * t / navigate in scope). Throw this on a 402 and let the calling component's
 * catch block call surfacePaymentError(err.payment, ...).
 */
export class QuotaError extends Error {
  readonly payment: PaymentError;
  constructor(payment: PaymentError) {
    super("payment_required");
    this.name = "QuotaError";
    this.payment = payment;
  }
}

export function isQuotaError(e: unknown): e is QuotaError {
  return e instanceof QuotaError;
}

/** Map a parsed 402 JSON body → typed PaymentError, or null if unrecognized. */
export function parsePaymentErrorBody(body: unknown): PaymentError | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (b.error === "quota_exceeded") {
    return {
      type: "quota_exceeded",
      kind: (b.kind === "tts" || b.kind === "live" || b.kind === "image" || b.kind === "stt" || b.kind === "email" || b.kind === "files" ? b.kind : "text") as QuotaKind,
      scope: b.scope === "daily" ? "daily" : "monthly",
      used: typeof b.used === "number" ? b.used : 0,
      limit: typeof b.limit === "number" ? b.limit : 0,
      periodEnd: typeof b.period_end === "string" ? b.period_end : "",
      currentTier: asPlanTier(b.current_tier),
      upgradeTier: b.upgrade_tier == null ? null : asPlanTier(b.upgrade_tier),
    };
  }
  if (b.error === "module_locked") {
    return {
      type: "module_locked",
      module: typeof b.module === "string" ? b.module : "",
      currentTier: asPlanTier(b.current_tier),
      upgradeTier: b.upgrade_tier == null ? null : asPlanTier(b.upgrade_tier),
    };
  }
  return null;
}

/** Path A — raw fetch Response. Returns null unless it's a parseable 402. */
export async function parsePaymentErrorFromResponse(
  res: Response,
): Promise<PaymentError | null> {
  if (res.status !== 402) return null;
  try {
    const body = await res.clone().json();
    return parsePaymentErrorBody(body);
  } catch {
    return null;
  }
}

/** Path B — supabase.functions.invoke() error (FunctionsHttpError.context). */
export async function parsePaymentErrorFromInvokeError(
  error: unknown,
): Promise<PaymentError | null> {
  const ctx = (error as { context?: unknown } | null | undefined)?.context;
  if (!ctx) return null;
  // ctx is normally the raw Response; tolerate an already-parsed object too.
  const maybeRes = ctx as { status?: number; json?: () => Promise<unknown>; clone?: () => Response };
  try {
    if (typeof maybeRes.json === "function") {
      if (typeof maybeRes.status === "number" && maybeRes.status !== 402) return null;
      const src = typeof maybeRes.clone === "function" ? maybeRes.clone() : maybeRes;
      const body = await (src as { json: () => Promise<unknown> }).json();
      return parsePaymentErrorBody(body);
    }
    return parsePaymentErrorBody(ctx);
  } catch {
    return null;
  }
}

// UX surfacing lives in src/hooks/usePaymentErrorHandler.ts (needs React
// context for the global PaywallSheet). This module stays pure parsing.
